import React, { useEffect, useMemo, useState } from "react";
import API from "./api";

const PAGE_SIZE = 24;
const STATUS_OPTIONS = [
  { value: "open", label: "Open" },
  { value: "resolved", label: "Resolved" },
  { value: "all", label: "All" },
];
const ALERT_TYPE_OPTIONS = [
  { value: "all", label: "All Alerts" },
  { value: "payment", label: "Payment Alerts" },
  { value: "follow_up", label: "Follow-up Alerts" },
];

const C = {
  page: "#fff7f5",
  card: "rgba(255,255,255,0.94)",
  text: "#252935",
  muted: "#6b6f7d",
  accent: "#d73c2e",
  accentDark: "#b9343a",
  border: "rgba(185,52,58,0.14)",
  soft: "#fff3f0",
  high: "#d73c2e",
  medium: "#cc7a00",
};

export default function ProfileAlerts({
  onBack,
  onViewProfile,
  canViewProfile = true,
  initialAlertType = "all",
}) {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState({ high: 0, medium: 0, total_open: 0, renewal_alerts: 0, follow_up_alerts: 0 });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState("open");
  const [alertTypeFilter, setAlertTypeFilter] = useState(normalizeAlertTypeFilter(initialAlertType));

  useEffect(() => {
    loadAlerts(currentPage, statusFilter, alertTypeFilter);
  }, [currentPage, statusFilter, alertTypeFilter]);

  useEffect(() => {
    setCurrentPage(1);
    setAlertTypeFilter(normalizeAlertTypeFilter(initialAlertType));
  }, [initialAlertType]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const headerStats = useMemo(
    () => [
      { label: "Open Alerts", value: total, icon: <AlertIcon size={22} color={C.accent} /> },
      { label: "Payment Alerts", value: stats.renewal_alerts, icon: <SeverityIcon size={22} color={C.high} /> },
      { label: "Follow-up Alerts", value: stats.follow_up_alerts, icon: <SeverityIcon size={22} color={C.medium} /> },
    ],
    [stats, total]
  );

  async function loadAlerts(page = 1, status = "open", alertType = "all") {
    setLoading(true);
    setError("");

    try {
      const params = new URLSearchParams({
        include_meta: "1",
        status,
        alert_type: alertType,
        limit: String(PAGE_SIZE),
        offset: String(Math.max(0, (page - 1) * PAGE_SIZE)),
      });
      const res = await API.get(`/profile-alerts?${params.toString()}`);
      const payload = Array.isArray(res.data) ? { items: res.data, total: res.data.length } : (res.data || {});
      const nextItems = safeArray(payload.items);
      const nextTotal = Number(payload.total || 0);
      const nextTotalPages = Math.max(1, Math.ceil(nextTotal / PAGE_SIZE));

      if (page > nextTotalPages) {
        setCurrentPage(nextTotalPages);
        return;
      }

      setItems(nextItems);
      setTotal(nextTotal);
      setStats({
        high: Number(payload.stats?.high || 0),
        medium: Number(payload.stats?.medium || 0),
        total_open: Number(payload.stats?.total_open || 0),
        renewal_alerts: Number(payload.stats?.renewal_alerts || 0),
        follow_up_alerts: Number(payload.stats?.follow_up_alerts || 0),
      });
    } catch (err) {
      console.error("Failed to load profile alerts", err);
      setError(err.response?.data?.error || "Failed to load profile alerts.");
      setItems([]);
      setTotal(0);
      setStats({ high: 0, medium: 0, total_open: 0, renewal_alerts: 0, follow_up_alerts: 0 });
    } finally {
      setLoading(false);
    }
  }

  return (
    <div style={S.page}>
      <style>{`
        @media (max-width: 860px) {
          .jk-profile-alerts-hero {
            flex-direction: column !important;
            align-items: flex-start !important;
          }
          .jk-profile-alerts-stat-grid {
            grid-template-columns: 1fr !important;
          }
          .jk-profile-alert-row {
            grid-template-columns: 1fr !important;
          }
        }
      `}</style>

      <div style={S.hero} className="jk-profile-alerts-hero">
        <div>
          <p style={S.eyebrow}>Alert Workspace</p>
          <h1 style={S.title}>Profile Alerts</h1>
          <p style={S.subtitle}>
            Open alerts are shown newest first. Renewal and follow-up alerts stay here until the linked package or note is updated.
          </p>
        </div>

        <div style={S.heroActions}>
          <button type="button" style={S.backBtn} onClick={onBack}>
            Back to Search
          </button>
        </div>
      </div>

      <div style={S.filterBar}>
        {STATUS_OPTIONS.map((option) => {
          const active = option.value === statusFilter;
          return (
            <button
              key={option.value}
              type="button"
              style={{
                ...S.filterChip,
                ...(active ? S.filterChipActive : {}),
              }}
              onClick={() => {
                if (option.value === statusFilter) return;
                setCurrentPage(1);
                setStatusFilter(option.value);
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div style={S.filterBar}>
        {ALERT_TYPE_OPTIONS.map((option) => {
          const active = option.value === alertTypeFilter;
          return (
            <button
              key={option.value}
              type="button"
              style={{
                ...S.filterChip,
                ...(active ? S.filterChipActive : {}),
              }}
              onClick={() => {
                if (option.value === alertTypeFilter) return;
                setCurrentPage(1);
                setAlertTypeFilter(option.value);
              }}
            >
              {option.label}
            </button>
          );
        })}
      </div>

      <div style={S.statGrid} className="jk-profile-alerts-stat-grid">
        {headerStats.map((item) => (
          <div key={item.label} style={S.statCard}>
            <span style={S.statBadge}>{item.icon}</span>
            <div style={S.statContent}>
              <b style={S.statValue}>{item.value}</b>
              <span style={S.statLabel}>{item.label}</span>
            </div>
          </div>
        ))}
      </div>

      {error && <div style={S.errorBox}>{error}</div>}

      {loading ? (
        <Splash
          title="Loading profile alerts"
          subtitle={
            statusFilter === "resolved"
              ? "Pulling the latest resolved alert activity."
              : statusFilter === "all"
                ? "Pulling the latest alert activity."
                : alertTypeFilter === "payment"
                  ? "Pulling the latest open payment alert activity."
                  : alertTypeFilter === "follow_up"
                    ? "Pulling the latest open follow-up alert activity."
                    : "Pulling the latest open alert activity."
          }
        />
      ) : items.length === 0 ? (
        <Splash
          title={statusFilter === "resolved" ? "No resolved alerts" : statusFilter === "all" ? "No alerts found" : "No open alerts"}
          subtitle={
            statusFilter === "resolved"
              ? "There are no resolved profile alerts in your current access scope."
              : statusFilter === "all"
                ? "There are no profile alerts in your current access scope."
                : "There are no active profile alerts in your current access scope."
          }
        />
      ) : (
        <>
          <div style={S.list}>
            {items.map((item) => (
              <AlertRow
                key={item.id}
                item={item}
                canViewProfile={canViewProfile}
                onView={() => {
                  if (typeof onViewProfile === "function") {
                    onViewProfile(item.profile_id, { initialTab: "crm" });
                  }
                }}
              />
            ))}
          </div>

          <div style={S.paginationBar}>
            <button
              type="button"
              style={{ ...S.paginationBtn, ...(currentPage <= 1 ? S.paginationBtnDisabled : {}) }}
              disabled={currentPage <= 1 || loading}
              onClick={() => setCurrentPage((page) => Math.max(1, page - 1))}
            >
              Previous
            </button>

            <span style={S.paginationInfo}>
              Page {currentPage} of {totalPages} | {total} {statusFilter === "all" ? "alerts" : `${statusFilter} alerts`}
            </span>

            <button
              type="button"
              style={{ ...S.paginationBtn, ...(currentPage >= totalPages ? S.paginationBtnDisabled : {}) }}
              disabled={currentPage >= totalPages || loading}
              onClick={() => setCurrentPage((page) => Math.min(totalPages, page + 1))}
            >
              Next
            </button>
          </div>
        </>
      )}
    </div>
  );
}

function AlertRow({ item, onView, canViewProfile }) {
  const severity = normalizeSeverity(item?.severity);
  const location = item?.location_display_cache || [item?.city, item?.state].filter(Boolean).join(", ");
  const profileLabel = [item?.profile_no ? `#${item.profile_no}` : "", item?.full_name || "Unnamed profile"]
    .filter(Boolean)
    .join(" ");
  const persona = String(item?.persona_tier_cache || "").trim();
  const notePreview = summarizeText(item?.note_content, 132);

  return (
    <div
      style={{ ...S.alertRow, ...(canViewProfile ? S.alertRowClickable : {}) }}
      className="jk-profile-alert-row"
      onClick={canViewProfile ? onView : undefined}
    >
      <div style={S.alertMain}>
        <div style={S.alertTopRow}>
          <span style={S.profileBadge}>{profileLabel}</span>
          <span
            style={{
              ...S.severityBadge,
              ...(severity === "high" ? S.severityBadgeHigh : S.severityBadgeMedium),
            }}
          >
            {labelize(severity || "open")}
          </span>
        </div>
        <h3 style={S.alertTitle}>{item?.message || labelize(item?.alert_type) || "Profile alert"}</h3>
        <div style={S.alertMeta}>
          {[
            persona ? `${persona} package` : "",
            location,
            item?.follow_up_at ? `Follow-up due ${formatDateTime(item.follow_up_at)}` : "",
            item?.related_entity_type ? `Linked to ${labelize(item.related_entity_type)}` : "",
          ]
            .filter(Boolean)
            .join(" | ")}
        </div>
        <div style={S.alertMeta}>
          {[item?.created_at ? `Created ${formatDateTime(item.created_at)}` : "", labelize(item?.status || "open")]
            .filter(Boolean)
            .join(" | ")}
        </div>
        {notePreview ? <div style={S.alertNotePreview}>{notePreview}</div> : null}
      </div>

      {canViewProfile ? (
        <button
          type="button"
          style={S.viewBtn}
          onClick={(event) => {
            event.stopPropagation();
            onView();
          }}
        >
          Open Profile
        </button>
      ) : null}
    </div>
  );
}

function Splash({ title, subtitle }) {
  return (
    <div style={S.splash}>
      <div style={S.splashOrb} />
      <p style={S.splashTitle}>{title}</p>
      <p style={S.splashSub}>{subtitle}</p>
    </div>
  );
}

function AlertIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3.5a4.5 4.5 0 0 0-4.5 4.5v2.1c0 .77-.22 1.53-.63 2.18L5.7 14.1c-.44.7.06 1.6.89 1.6h10.82c.83 0 1.33-.9.89-1.6l-1.17-1.82a4.13 4.13 0 0 1-.63-2.18V8A4.5 4.5 0 0 0 12 3.5Z"
        stroke={color}
        strokeWidth="1.8"
        strokeLinejoin="round"
      />
      <path d="M10.2 18a1.9 1.9 0 0 0 3.6 0" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <path d="M12 8.1v2.85" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="12.95" r=".8" fill={color} />
    </svg>
  );
}

function SeverityIcon({ size = 20, color = "currentColor" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path d="M12 4.5 4.8 18h14.4L12 4.5Z" stroke={color} strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M12 9.2v4.4" stroke={color} strokeWidth="1.8" strokeLinecap="round" />
      <circle cx="12" cy="16.4" r=".85" fill={color} />
    </svg>
  );
}

function normalizeSeverity(value) {
  const normalized = String(value || "").trim().toLowerCase();
  return normalized === "high" ? "high" : "medium";
}

function normalizeAlertTypeFilter(value) {
  const normalized = String(value || "").trim().toLowerCase();
  if (!normalized || normalized === "all") return "all";
  if (["payment", "renewal", "renewal_due"].includes(normalized)) return "payment";
  if (["follow_up", "followup", "follow_up_due"].includes(normalized)) return "follow_up";
  return "all";
}

function labelize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\w\S*/g, (part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase());
}

function safeArray(value) {
  return Array.isArray(value) ? value : [];
}

function formatDateTime(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return new Intl.DateTimeFormat("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  }).format(date);
}

function summarizeText(value, maxLength = 120) {
  const normalized = String(value || "").replace(/\s+/g, " ").trim();
  if (!normalized) return "";
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, Math.max(0, maxLength - 1)).trimEnd()}…`;
}

const S = {
  page: {
    minHeight: "100%",
    background: `linear-gradient(180deg, ${C.page} 0%, #ffffff 100%)`,
    padding: "28px 18px 60px",
  },
  hero: {
    maxWidth: 1180,
    margin: "0 auto 20px",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 20,
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 34,
    padding: "28px 32px",
    boxShadow: "0 18px 60px rgba(185,52,58,0.10)",
  },
  eyebrow: {
    margin: 0,
    color: C.accent,
    letterSpacing: 3,
    textTransform: "uppercase",
    fontSize: 12,
    fontWeight: 900,
  },
  title: {
    margin: "10px 0 8px",
    fontSize: 44,
    lineHeight: 1.02,
    color: C.text,
    fontWeight: 950,
  },
  subtitle: {
    margin: 0,
    maxWidth: 620,
    color: C.muted,
    fontSize: 16,
    lineHeight: 1.7,
    fontWeight: 600,
  },
  heroActions: {
    display: "flex",
    alignItems: "center",
    gap: 12,
  },
  filterBar: {
    maxWidth: 1180,
    margin: "0 auto 20px",
    display: "flex",
    alignItems: "center",
    gap: 10,
    flexWrap: "wrap",
  },
  filterChip: {
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  filterChipActive: {
    background: C.accent,
    color: "#fff",
    borderColor: C.accent,
  },
  backBtn: {
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    borderRadius: 999,
    padding: "12px 18px",
    fontSize: 14,
    fontWeight: 900,
    cursor: "pointer",
  },
  statGrid: {
    maxWidth: 1180,
    margin: "0 auto 20px",
    display: "grid",
    gap: 16,
    gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
  },
  statCard: {
    display: "flex",
    alignItems: "center",
    gap: 16,
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    padding: "22px 24px",
    boxShadow: "0 18px 60px rgba(185,52,58,0.08)",
  },
  statBadge: {
    width: 60,
    height: 60,
    borderRadius: "50%",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    background: C.soft,
    border: `1px solid ${C.border}`,
    flexShrink: 0,
  },
  statContent: {
    display: "grid",
    gap: 4,
  },
  statValue: {
    fontSize: 32,
    lineHeight: 1,
    color: C.accentDark,
    fontWeight: 950,
  },
  statLabel: {
    fontSize: 15,
    color: C.text,
    fontWeight: 800,
  },
  errorBox: {
    maxWidth: 1180,
    margin: "0 auto 20px",
    borderRadius: 18,
    border: `1px solid ${C.border}`,
    background: "#fff1ee",
    color: C.accent,
    padding: "14px 16px",
    fontWeight: 700,
  },
  list: {
    maxWidth: 1180,
    margin: "0 auto",
    display: "grid",
    gap: 14,
  },
  alertRow: {
    display: "grid",
    gridTemplateColumns: "1fr auto",
    gap: 18,
    alignItems: "center",
    background: C.card,
    border: `1px solid ${C.border}`,
    borderRadius: 28,
    padding: "22px 24px",
    boxShadow: "0 18px 60px rgba(185,52,58,0.08)",
  },
  alertRowClickable: {
    cursor: "pointer",
  },
  alertMain: {
    minWidth: 0,
    display: "grid",
    gap: 8,
  },
  alertTopRow: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    flexWrap: "wrap",
  },
  profileBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    background: C.soft,
    border: `1px solid ${C.border}`,
    color: C.text,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 900,
  },
  severityBadge: {
    display: "inline-flex",
    alignItems: "center",
    borderRadius: 999,
    padding: "6px 12px",
    fontSize: 12,
    fontWeight: 900,
  },
  severityBadgeHigh: {
    background: "#fff1ee",
    color: C.high,
  },
  severityBadgeMedium: {
    background: "#fff8ea",
    color: C.medium,
  },
  alertTitle: {
    margin: 0,
    color: C.text,
    fontSize: 20,
    lineHeight: 1.45,
    fontWeight: 900,
  },
  alertMeta: {
    color: C.muted,
    fontSize: 13,
    lineHeight: 1.6,
    fontWeight: 700,
  },
  alertNotePreview: {
    color: C.text,
    fontSize: 13,
    lineHeight: 1.7,
    fontWeight: 700,
  },
  viewBtn: {
    border: "none",
    background: C.text,
    color: "#fff",
    borderRadius: 999,
    padding: "12px 18px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
    whiteSpace: "nowrap",
  },
  paginationBar: {
    maxWidth: 1180,
    margin: "22px auto 0",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 16,
  },
  paginationBtn: {
    border: `1px solid ${C.border}`,
    background: "#fff",
    color: C.text,
    borderRadius: 999,
    padding: "10px 16px",
    fontSize: 13,
    fontWeight: 900,
    cursor: "pointer",
  },
  paginationBtnDisabled: {
    opacity: 0.45,
    cursor: "not-allowed",
  },
  paginationInfo: {
    color: C.muted,
    fontSize: 13,
    fontWeight: 800,
  },
  splash: {
    maxWidth: 1180,
    margin: "0 auto",
    minHeight: 320,
    borderRadius: 34,
    border: `1px solid ${C.border}`,
    background: C.card,
    display: "grid",
    placeItems: "center",
    textAlign: "center",
    padding: 24,
    position: "relative",
    overflow: "hidden",
  },
  splashOrb: {
    position: "absolute",
    width: 320,
    height: 320,
    borderRadius: "50%",
    background: "radial-gradient(circle, rgba(215,60,46,0.13), rgba(215,60,46,0))",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
  },
  splashTitle: {
    margin: 0,
    color: C.text,
    fontSize: 26,
    fontWeight: 950,
    position: "relative",
  },
  splashSub: {
    margin: "8px 0 0",
    color: C.muted,
    fontSize: 15,
    lineHeight: 1.7,
    maxWidth: 520,
    fontWeight: 700,
    position: "relative",
  },
};
